import { patientApi } from '@/api/PatientApi'
import { useCompleteCardSetup } from '@/api/hooks/useUpdatePrimaryCard'
import Config from '@/config'
import { colors } from '@/constants/colors'
import { logger } from '@/lib/logger'
import { Button, ButtonText } from '@/ui/core/button'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useIsDesktop } from '@/ui/hooks/useIsDesktop'
import {
	CardCvcElement,
	CardExpiryElement,
	CardNumberElement,
	Elements,
	ExpressCheckoutElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { Asset } from 'expo-asset'
import { useMemo, useState } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'

/**
 * Stripe's fields live in its own iframe, so the brand font has to be handed to it by URL. The
 * bundled Maison Neue Book is served from this origin; the iframe fetches it as a font-face.
 * Should that fetch be refused, Stripe falls back through the stack to a system sans rather than
 * to a third-party face, which is why Lato from Google Fonts is gone.
 */
const bookFontUrl = () => {
	const uri = Asset.fromModule(require('#/assets/fonts/MaisonNeue-Book.otf')).uri
	return uri.startsWith('/') && typeof window !== 'undefined'
		? `${window.location.origin}${uri}`
		: uri
}

const ELEMENTS_OPTIONS = {
	fonts: [{ family: 'MaisonNeue-Book', src: `url(${bookFontUrl()})` }],
}

const FONT_STACK = 'MaisonNeue-Book, -apple-system, system-ui, sans-serif'

const generateCardNumberStyleOptions = ({ isDesktop }: { isDesktop: boolean }) => ({
	showIcon: false,
	placeholder: 'Card Number',
	classes: {
		base: 'bg-white px-4 py-3 rounded-brand border border-edge',
	},
	style: {
		base: {
			fontSize: isDesktop ? '16px' : '14px',
			fontFamily: FONT_STACK,
			color: colors.ink,
			'::placeholder': {
				color: colors.muted,
			},
		},
		invalid: {
			color: colors.danger,
		},
	},
})

const generateCardMetadataOptions = ({ isDesktop }: { isDesktop: boolean }) => ({
	classes: {
		base: 'bg-white px-4 py-3 rounded-brand border border-edge w-1/2',
	},
	style: {
		base: {
			fontSize: isDesktop ? '16px' : '14px',
			fontFamily: FONT_STACK,
			color: colors.ink,
			'::placeholder': {
				color: colors.muted,
			},
		},
		invalid: {
			color: colors.danger,
		},
	},
})

const BillingCardFormInternal = ({ onComplete = () => {} }) => {
	const [isSaving, setIsSaving] = useState(false)
	const [isCardNumberComplete, setIsCardNumberComplete] = useState(false)
	const [isCardExpiryComplete, setIsCardExpiryComplete] = useState(false)
	const [isCardCvcComplete, setIsCardCvcComplete] = useState(false)
	const isDesktop = useIsDesktop()
	const { mutateAsync: completeCardSetup } = useCompleteCardSetup()
	const stripe = useStripe()
	const elements = useElements()

	const isCardComplete = useMemo(
		() => isCardNumberComplete && isCardCvcComplete && isCardExpiryComplete,
		[isCardNumberComplete, isCardExpiryComplete, isCardCvcComplete]
	)

	const onCardNumberChange = ({ complete }: { complete: boolean }) => {
		setIsCardNumberComplete(complete)
	}

	const onCardExpiryChange = ({ complete }: { complete: boolean }) => {
		setIsCardExpiryComplete(complete)
	}

	const onCardCvcChange = ({ complete }: { complete: boolean }) => {
		setIsCardCvcComplete(complete)
	}

	const onCardSave = async () => {
		setIsSaving(true)
		const card = elements?.getElement(CardNumberElement)
		if (!stripe || !card) {
			setIsSaving(false)
			return
		}
		try {
			// The same SetupIntent the native sheet confirms; the web confirms it with its elements.
			const setup = await patientApi.startCardSetup({})
			const { setupIntent, error } = await stripe.confirmCardSetup(setup.clientSecret, {
				payment_method: { card },
			})
			if (error || setupIntent?.status !== 'succeeded') {
				throw new Error(error?.message ?? `setup_intent_${setupIntent?.status}`)
			}
			await completeCardSetup({ setupIntentId: setup.setupIntentId })
			onComplete()
		} catch (error) {
			logger.error(new Error('failed_to_set_up_card', { cause: error }))
			Toast.show({
				type: 'error',
				text1: 'Failed to update card, check your card and try again',
			})
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<VStack gap={64}>
			<VStack gap={20}>
				<CardNumberElement
					onChange={onCardNumberChange}
					options={generateCardNumberStyleOptions({ isDesktop })}
				/>
				<HStack gap={20}>
					<CardExpiryElement
						onChange={onCardExpiryChange}
						options={generateCardMetadataOptions({ isDesktop })}
					/>
					<CardCvcElement
						onChange={onCardCvcChange}
						options={generateCardMetadataOptions({ isDesktop })}
					/>
				</HStack>
			</VStack>
			<Button
				fullWidth
				isLoading={isSaving}
				onPress={onCardSave}
				disabled={!isCardComplete}
			>
				<ButtonText>Save</ButtonText>
			</Button>
		</VStack>
	)
}

/**
 * Apple Pay and Google Pay on the web, in Stripe's express button. It needs its own Elements
 * group in setup mode, since a wallet is confirmed against the group rather than one field.
 * The button only renders where a wallet exists, so on a browser with neither, nothing shows.
 */
const Wallets = ({ onComplete }: { onComplete: () => void }) => {
	const stripe = useStripe()
	const elements = useElements()
	const { mutateAsync: completeCardSetup } = useCompleteCardSetup()
	const [hasWallet, setHasWallet] = useState(false)

	const onConfirm = async () => {
		if (!stripe || !elements) return
		try {
			const submitted = await elements.submit()
			if (submitted.error) throw new Error(submitted.error.message)
			const setup = await patientApi.startCardSetup({})
			const { setupIntent, error } = await stripe.confirmSetup({
				elements,
				clientSecret: setup.clientSecret,
				redirect: 'if_required',
			})
			if (error || setupIntent?.status !== 'succeeded') {
				throw new Error(error?.message ?? `setup_intent_${setupIntent?.status}`)
			}
			await completeCardSetup({ setupIntentId: setup.setupIntentId })
			onComplete()
		} catch (error) {
			logger.error(new Error('failed_to_set_up_card', { cause: error }))
			Toast.show({
				type: 'error',
				text1: 'Failed to update card, check your card and try again',
			})
		}
	}

	return (
		<VStack gap={20} className={hasWallet ? undefined : 'hidden'}>
			<ExpressCheckoutElement
				options={{ buttonTheme: { applePay: 'black', googlePay: 'black' } }}
				onReady={({ availablePaymentMethods }) => setHasWallet(!!availablePaymentMethods)}
				onConfirm={onConfirm}
			/>
			<HStack gap={12} className="items-center">
				<View className="h-px flex-1" style={{ backgroundColor: colors.edge }} />
				<Text size="body2" tone="soft">
					or use a card
				</Text>
				<View className="h-px flex-1" style={{ backgroundColor: colors.edge }} />
			</HStack>
		</VStack>
	)
}

const BillingCardForm = ({ onComplete = () => {} }) => {
	const stripe = useMemo(() => loadStripe(Config.STRIPE_PUBLISHABLE_KEY), [])
	return (
		<VStack gap={24}>
			<Elements
				stripe={stripe}
				options={{ ...ELEMENTS_OPTIONS, mode: 'setup', currency: 'usd' }}
			>
				<Wallets onComplete={onComplete} />
			</Elements>
			<Elements stripe={stripe} options={ELEMENTS_OPTIONS}>
				<BillingCardFormInternal onComplete={onComplete} />
			</Elements>
		</VStack>
	)
}

export { BillingCardForm }
